import { Button } from "@mui/material"
import { DrivingExpirienceDto } from "api"
import VideoPlayer from "common/VideoPlayer"
import { trackSpinModelPageClicks } from "common/analytics"
import NextImage from "common/bit/NextImage"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import styled, { keyframes } from "styled-components"
import { A11y, Keyboard, Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import colors from "theme/colors"
import { TypographyCenter } from "theme/shared"
import SwiperControllerRow from "../../safety/SwiperControllerRow"

const StyledWrap = styled.div`
  height: 90vh;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 450px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-bottom: 40px;
  }
`

const StyledSwiperControllerRow = styled(SwiperControllerRow)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;
  margin-top: 41px;
`

const StyledSwiper = styled(Swiper).attrs({})`
  width: 100%;
`

const zoomInAndOut = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`
const StyledImageContainer = styled.div<{ $zoomIn: boolean }>`
  width: 100%;
  aspect-ratio: 360/240;
  position: relative;
  overflow: hidden;
  img {
    width: 250px;
    height: 250px;
    animation: ${(p) => (p.$zoomIn ? zoomInAndOut : "none")} 1s ease-in-out;
  }
  video {
    clip-path: inset(1px 1px);
  }
`
const StyledTextContainer = styled.div`
  ${(p) => p.theme.breakpoints.down("sm")} {
    display: flex;
    flex-direction: column;
    padding: 0 16px;
  }
`
enum GifEnum {
  NORMAL = "/img/knob/media-knob-inactive.png",
  TURN = "/img/knob/media-knob-turn-single.gif",
  CLICK = "/img/knob/media-knob-click-single.gif",
}

type FeaturesWrapperProps = {
  data: DrivingExpirienceDto
  openFeaturesModel: () => void
}

const FeaturesWrapper = ({ data, openFeaturesModel }: FeaturesWrapperProps) => {
  const [animating, setAnimating] = useState(false)
  const [gifSrc, setGifSrc] = useState(GifEnum.NORMAL)
  const navigationPrevRef = useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>
  const navigationNextRef = useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>

  const swiperRef = useRef<any>(null)

  useEffect(() => {
    // re load gif with time out
    const timeout = setTimeout(() => {
      setGifSrc(GifEnum.NORMAL)
    }, 1000)
    return () => clearTimeout(timeout)
  }, [gifSrc])

  const animatePic = () => {
    setGifSrc(GifEnum.CLICK)
    setAnimating(true)
    setTimeout(() => setAnimating(false), 1000)
    swiperRef?.current?.swiper.slideNext()
  }

  return (
    <StyledWrap>
      <StyledSwiper
        modules={[Navigation, A11y, Keyboard]}
        slidesPerView={1}
        centeredSlides
        keyboard={{
          enabled: true,
        }}
        navigation={{
          enabled: true,
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onSlideChange={() => {
          setGifSrc(GifEnum.TURN)
        }}
        loop
        ref={swiperRef}
      >
        {data?.displayComponents?.map((item, index) => (
          <SwiperSlide
            style={{ width: "max-content", height: "auto" }}
            key={index}
          >
            <div>
              <StyledImageContainer $zoomIn={animating}>
                {item?.videoUrl ? (
                  <VideoPlayer
                    url={item.videoUrl}
                    light={false}
                    label={"mazda"}
                    playingState={true}
                    loop={true}
                    imgAlt={item?.alt}
                    imgUrlDesktop={item?.imageUrl}
                    imgUrlMobile={item?.imageUrl}
                    cover={false}
                  />
                ) : (
                  <NextImage
                    image={{
                      imageUrl: item?.imageUrl,
                      alt: item?.alt,
                    }}
                    cover={false}
                  />
                )}
              </StyledImageContainer>
              <StyledTextContainer>
                <TypographyCenter variant={"h2"} $mb="10px" $mt="10px">
                  {item?.name}
                </TypographyCenter>
                <TypographyCenter variant="body1" $color={colors.disable}>
                  {item?.description}
                </TypographyCenter>
              </StyledTextContainer>
            </div>
          </SwiperSlide>
        ))}
      </StyledSwiper>
      <StyledSwiperControllerRow
        prevRef={navigationPrevRef}
        nextRef={navigationNextRef}
        trackEvent={trackSpinModelPageClicks}
      >
        <ButtonBaseBlank
          onClick={() => {
            animatePic()
            trackSpinModelPageClicks()
          }}
        >
          <Image
            src={gifSrc}
            alt="knob"
            width={94}
            height={86}
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
          />
        </ButtonBaseBlank>
      </StyledSwiperControllerRow>
      <Button variant="contained" onClick={openFeaturesModel}>
        לפיצ׳רים נוספים
      </Button>
    </StyledWrap>
  )
}

export default FeaturesWrapper
